<template>
  <PVForm
    v-slot="$form"
    class="workspace-form"
    :initial-values="initialValues"
    :resolver="resolver"
    @submit="onFormSubmit"
  >
    <div class="form-row">
      <div class="form-field">
        <label for="name">{{ $t('workspaceForm.name') }} *</label>
        <PVInputText :disabled="props.disabled" fluid name="name"></PVInputText>
        <PVMessage v-if="$form.name?.invalid" severity="error" variant="simple">
          {{ $form.name.error.message }}
        </PVMessage>
      </div>
      <div class="form-field">
        <label for="location">{{ $t('workspaceForm.location') }} *</label>
        <PVSelect
          fluid
          :disabled="props.disabled || isEditMode"
          :loading="isLoadingLocations"
          name="location"
          option-label="text"
          :options="locations"
          option-value="value"
        ></PVSelect>
        <PVMessage
          v-if="$form.location?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.location.error.message }}
        </PVMessage>
      </div>
    </div>
    <div class="form-row">
      <div class="form-field">
        <label for="duration">{{ $t('workspaceForm.durationStart') }} *</label>
        <PVDatePicker :disabled="props.disabled" fluid name="durationStart" />
        <PVMessage
          v-if="$form.durationStart?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.durationStart.error.message }}
        </PVMessage>
      </div>
      <div class="form-field">
        <label for="duration">{{ $t('workspaceForm.durationEnd') }} *</label>
        <PVDatePicker :disabled="props.disabled" fluid name="durationEnd" />
        <PVMessage
          v-if="$form.durationEnd?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.durationEnd.error.message }}
        </PVMessage>
      </div>
      <div v-if="isEditMode" class="form-field status">
        <label for="status">{{ $t('workspaceForm.status') }} *</label>
        <PVSelect
          :disabled="props.disabled"
          fluid
          name="status"
          :option-label="
            (value: WorkspaceStatus) =>
              t(`workspaceForm.workspaceStatus.${value}`)
          "
          :options="Object.values(WorkspaceStatus)"
        ></PVSelect>
        <PVMessage
          v-if="$form.status?.invalid"
          severity="error"
          variant="simple"
        >
          {{ $form.status.error.message }}
        </PVMessage>
      </div>
    </div>

    <!-- Slot for workspace owners selection table for workspace creation -->
    <slot></slot>

    <div class="buttons-container">
      <vf-button
        v-if="!props.disabled && isEditMode && isFormDirty($form)"
        :size="buttonSizes.large"
        :variant="ButtonVariant.PRIMARY"
        @click="$form.reset()"
        >{{ t('projectForm.cancel') }}</vf-button
      >
      <vf-button
        v-if="
          !props.disabled &&
          (isCreateMode || (isEditMode && isFormDirty($form)))
        "
        :busy="isLoading"
        :size="buttonSizes.large"
        type="submit"
        :variant="ButtonVariant.SECONDARY"
      >
        {{
          isEditMode
            ? t('generic.saveChanges')
            : t('workspaceForm.createWorkspace')
        }}
      </vf-button>
    </div>
  </PVForm>
</template>

<script lang="ts" setup>
  import { useFetch } from '@/composables/useFetch';
  import { ProjectDataService } from '@/services';
  import { FormFieldState, FormSubmitEvent } from '@primevue/forms/form';
  import { zodResolver } from '@primevue/forms/resolvers/zod';
  import PVSelect from 'primevue/select';
  import { WorkspaceStatus } from 'types';
  import { computed, reactive, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ButtonVariant } from '../VfButton/VfButton.types';
  import {
    WorkspaceFormFields,
    WorkspaceFormMode,
    WorkspaceFormProps,
  } from './WorkspaceForm.types';
  import { workspaceFormValidator } from './workspaceFormValidator';

  const props = withDefaults(defineProps<WorkspaceFormProps>(), {
    mode: WorkspaceFormMode.CREATE,
  });

  const emit = defineEmits<{
    (e: 'submit', workspaceFormData: WorkspaceFormFields): void;
  }>();

  const { t } = useI18n();

  const isCreateMode = computed<boolean>(
    () => props.mode === WorkspaceFormMode.CREATE,
  );
  const isEditMode = computed<boolean>(
    () => props.mode === WorkspaceFormMode.EDIT,
  );

  const workspaceFormFields = computed<WorkspaceFormFields>(() => {
    if (props.mode === WorkspaceFormMode.CREATE) {
      return {
        name: '',
        location: '',
        durationStart: new Date(),
        durationEnd: new Date(),
      };
    }
    return {
      name: props.initialValues.name,
      location: props.initialValues.location,
      durationStart: new Date(props.initialValues.startDate),
      durationEnd: new Date(props.initialValues.endDate),
      status: props.initialValues.status,
    };
  });

  const initialValues = reactive<WorkspaceFormFields>(
    workspaceFormFields.value,
  );
  const resolver = ref(zodResolver(workspaceFormValidator(t)));
  const { data: locations = [], isLoading: isLoadingLocations } = useFetch(
    ProjectDataService.getPossibleProjectLocationsForSelect,
  );

  function isFormDirty(form: Record<string, FormFieldState>) {
    return Object.values(form).some((field) => field.dirty);
  }

  function onFormSubmit(form: FormSubmitEvent): void {
    if (!form.valid) return;
    emit('submit', form.values as WorkspaceFormFields);
  }
</script>

<style scoped lang="scss">
  .form-row {
    display: flex;
    gap: 2rem;
    margin-bottom: 1.25rem;

    .form-field {
      width: 50%;
    }
  }

  .buttons-container {
    margin: 1rem;
    display: flex;
    &:has(> button:last-child:only-child) {
      justify-content: flex-end;
    }
    &:not(:has(> button:last-child:only-child)) {
      justify-content: space-between;
    }
  }
</style>
